import { useEffect, useMemo, useRef, useState } from "react";
import { audio } from "../fx/audio";
import { useFx } from "../fx/state";
import { GROUPS, groupOfKind } from "../lib/colors";
import type { ReplayDef } from "../lib/data";
import { fmtInt } from "../lib/format";
import { chapterAt, progressAt, replaySeconds } from "../lib/replay";
import { jumpToChapter, restartReplay, seekReplay, startReplay, stepChapter, togglePlay } from "../lib/replayEngine";
import { useApp } from "../lib/store";
import { formatInstant, yearsLabel } from "../lib/time";

const PERIODS: { label: string; to: number }[] = [
  { label: "Middle Ages", to: 1500 },
  { label: "1500–1789", to: 1789 },
  { label: "1789–1914", to: 1914 },
  { label: "1914–1945", to: 1945 },
  { label: "1945–1989", to: 1989 },
  { label: "Since 1989", to: Infinity },
];

/** The war the picker leads with. */
export const FEATURED = "ww2";

const minutes = (s: number) => (s < 75 ? "about a minute" : `about ${Math.round(s / 30) / 2} minutes`.replace(".5", "½"));
const runningTime = (r: ReplayDef) => (r.s ?? replaySeconds(r.t0, r.t1)) + r.ch.length * 4.5;

function useWarColour(r: ReplayDef): string {
  const c = useApp((s) => s.conflicts);
  return c ? GROUPS[groupOfKind(c.kind[r.i])].hex : GROUPS[0].hex;
}

/** The war's rhythm: its battles over time (the replay's pacing profile), as a filled line. */
function Pace({ r, w = 240, h = 40, ticks = false }: { r: ReplayDef; w?: number; h?: number; ticks?: boolean }) {
  const colour = useWarColour(r);
  const id = `pace-${r.id}-${w}`;
  const path = useMemo(() => {
    const p = r.pace && r.pace.length ? r.pace : [1];
    const max = Math.max(...p, 1e-9);
    const pts = p.map((v, k) => [(k / Math.max(1, p.length - 1)) * w, h - 2 - Math.sqrt(v / max) * (h - 6)]);
    const line = pts.map(([x, y], k) => `${k ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
    return { line, area: `${line}L${w},${h}L0,${h}Z` };
  }, [r, w, h]);
  return (
    <svg className="pace" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={colour} stopOpacity="0.55" />
          <stop offset="1" stopColor={colour} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={path.area} fill={`url(#${id})`} />
      <path d={path.line} fill="none" stroke={colour} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {ticks && r.ch.map((c, k) => {
        const x = ((c.t - r.t0) / Math.max(1e-9, r.t1 - r.t0)) * w;
        return <path key={k} d={`M${x},${h - 7}l3.5,3.5l-3.5,3.5l-3.5,-3.5z`} fill="var(--accent)" />;
      })}
    </svg>
  );
}

/** Choose a war: World War II first, then the rest by period; "Lithuania" narrows the list to Lithuania's wars. */
export function ReplayPicker() {
  const replays = useApp((s) => s.replays);
  const setPanel = useApp((s) => s.setPanel);
  const [lt, setLt] = useState(false);
  const inner = useRef<HTMLDivElement>(null);
  // keyboard and screen readers start in the dialog, and go back where they were when it closes
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    inner.current?.focus({ preventScroll: true });
    return () => { if (before?.isConnected && document.activeElement === document.body) before.focus({ preventScroll: true }); };
  }, []);
  const close = () => setPanel("moment");
  const featured = replays?.find((r) => r.id === FEATURED);
  const groups = useMemo(() => {
    const list = (replays ?? []).filter((r) => !lt || r.lt);
    let from = -Infinity;
    return PERIODS.map((p) => {
      const items = list.filter((r) => r.t0 >= from && r.t0 < p.to);
      from = p.to;
      return { label: p.label, items };
    }).filter((g) => g.items.length);
  }, [replays, lt]);
  const play = (r: ReplayDef) => { audio.unlock(); startReplay({ def: r }); };
  return (
    <div className="picker" data-testid="replay-list" role="dialog" aria-modal="true" aria-label="Replays">
      <div className="pk-inner" ref={inner} tabIndex={-1}>
        <header className="pk-head">
          <div>
            <div className="kicker">Replays</div>
            <h1>Watch the wars that shaped the world</h1>
            <p className="pk-lead">Battles flare up where and when they happened, borders move, and the camera stops at each turning point. Turn the sound on.</p>
          </div>
          <button className="pk-close" onClick={close} aria-label="Close replays">×</button>
        </header>
        {featured && !lt && (
          <button className="pk-hero" onClick={() => play(featured)} aria-label={`Play ${featured.title}`}>
            <span className="pk-hero-text">
              <span className="kicker">Start here</span>
              <span className="pk-hero-title">{featured.title}</span>
              <span className="pk-hero-meta">{yearsLabel(featured.t0, featured.t1, false)} · {featured.ch.length} chapters · {minutes(runningTime(featured))}</span>
              <span className="pk-hero-blurb">{featured.blurb}</span>
              <span className="pk-hero-play"><span aria-hidden>▶</span> Play</span>
            </span>
            <span className="pk-hero-pace"><Pace r={featured} w={420} h={110} ticks /></span>
          </button>
        )}
        <div className="pk-filter" role="group" aria-label="Show">
          <button aria-pressed={!lt} onClick={() => setLt(false)}>All wars</button>
          <button aria-pressed={lt} onClick={() => setLt(true)}>Lithuania</button>
        </div>
        {!replays && <p className="loading">Loading…</p>}
        {groups.map((g) => (
          <section key={g.label} className="pk-group">
            <h2>{g.label}</h2>
            <ul className="pk-grid">
              {g.items.map((r) => (
                <li key={r.id}>
                  <button className="pk-card" onClick={() => play(r)}>
                    <span className="pk-card-title">{r.title}</span>
                    <span className="pk-card-meta">{yearsLabel(r.t0, r.t1, false)} · {r.ch.length} chapters · {minutes(runningTime(r))}</span>
                    {r.blurb && <span className="pk-card-blurb">{r.blurb}</span>}
                    <span className="pk-card-pace"><Pace r={r} /></span>
                    <span className="pk-card-play" aria-hidden>▶</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="fineprint">Any other war can be replayed from its page: pick it on the map or the timeline, then ▶ Replay. The curve on each card is the war's rhythm: its recorded battles over time.</p>
      </div>
    </div>
  );
}

/** Side panel during a replay: its chapters (click one to go there), then the war's own page. */
export function ReplayPanel() {
  const r = useApp((s) => s.replay)!;
  const t = useApp((s) => s.t);
  const endReplay = useApp((s) => s.endReplay);
  const cur = chapterAt(r.chapters, t);
  const list = useRef<HTMLOListElement>(null);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>("li.current")?.scrollIntoView({ block: "nearest" });
  }, [cur]);
  return (
    <section className="replay-panel" data-testid="replay-panel" aria-label={`Replay: ${r.title}`}>
      <div className="rp-head">
        <span className="rp-label">Replay</span>
        <button className="rp-exit" onClick={endReplay}>Exit replay</button>
      </div>
      {r.chapters.length > 0 ? (
        <ol className="rp-chapters" ref={list}>
          {r.chapters.map((ch, k) => (
            <li key={k} className={k === cur ? "current" : k < cur ? "past" : ""} aria-current={k === cur ? "step" : undefined}>
              <button onClick={() => jumpToChapter(k)}>
                <span className="rp-date">{ch.d}</span>
                <span className="rp-text">{ch.x}</span>
              </button>
              {ch.u && <a className="rp-src" href={ch.u} target="_blank" rel="noopener">Source</a>}
            </li>
          ))}
        </ol>
      ) : (
        <p className="fineprint">This war has no recorded battles to pause at; it plays at a steady pace.</p>
      )}
    </section>
  );
}

const Icon = ({ d }: { d: string }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden><path d={d} fill="currentColor" /></svg>
);
const PLAY = "M4 2.5v11l9-5.5z", PAUSE = "M3.5 2.5h3v11h-3zM9.5 2.5h3v11h-3z", PREV = "M3 2.5h2v11H3zM14 2.5v11L6 8z",
  NEXT = "M11 2.5h2v11h-2zM2 2.5v11L10 8z";
const SPEAKER = "M2 6h3l4-3.5v11L5 10H2z", WAVES = "M11 5.2a4 4 0 0 1 0 5.6M12.8 3.4a6.5 6.5 0 0 1 0 9.2", MUTE = "M11 5.5l4 5M15 5.5l-4 5";

/** Sound on and off (it starts with a click: browsers keep pages quiet until then). */
export function SoundButton({ className = "" }: { className?: string }) {
  const sound = useFx((s) => s.sound);
  const blocked = useFx((s) => s.audioBlocked);
  const level = useFx((s) => s.level);
  const setSound = useFx((s) => s.setSound);
  if (level === "off") return null;
  const label = sound ? (blocked ? "Sound: click to start" : "Mute") : "Sound on";
  return (
    <button className={`sound-btn ${className}${blocked && sound ? " blocked" : ""}`} aria-label={label} title={label} aria-pressed={sound}
      onClick={() => { const on = !sound || blocked; setSound(on); if (on) { audio.setEnabled(true, useFx.getState().volume); audio.unlock(); } }}>
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
        <path d={SPEAKER} fill="currentColor" />
        <path d={sound ? WAVES : MUTE} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    </button>
  );
}

/** A caption that types itself out (the full text is there at once for screen readers and search). */
function Typewriter({ text, live }: { text: string; live: boolean }) {
  const words = useMemo(() => text.split(" "), [text]);
  const sound = useFx((s) => s.sound);
  useEffect(() => {
    if (!live || !sound) return;
    const timers: number[] = [];
    for (let i = 0; i < text.length; i += 3) timers.push(window.setTimeout(() => audio.tick(), i * 16));
    return () => timers.forEach((x) => window.clearTimeout(x));
  }, [text, live, sound]);
  let n = 0;
  return (
    <p className={`rh-text${live ? " typing" : ""}`}>
      {words.map((w, k) => {
        const start = n;
        n += w.length + 1;
        return (
          <span key={k}>
            <span className="tw">{Array.from(w).map((ch, j) => <span key={j} style={{ animationDelay: `${(start + j) * 16}ms` }}>{ch}</span>)}</span>
            {k < words.length - 1 ? " " : ""}
          </span>
        );
      })}
    </p>
  );
}

/** On the map during a replay: the war clock, the current chapter, the replay's controls. */
export function ReplayOverlay() {
  const r = useApp((s) => s.replay);
  const t = useApp((s) => s.t);
  const playing = useApp((s) => s.playing);
  const speed = useApp((s) => s.speed);
  const setSpeed = useApp((s) => s.setSpeed);
  const endReplay = useApp((s) => s.endReplay);
  const stats = useFx((s) => s.stats);
  const fxOn = useFx((s) => s.level !== "off");
  if (!r) return null;
  const k = chapterAt(r.chapters, t);
  const ch = k >= 0 ? r.chapters[k] : null;
  const u = progressAt(r.warp, t);
  const live = !!ch && r.hold > 0 && Math.abs(t - ch.t) < 1e-6;
  const day = Math.max(1, Math.floor((Math.min(t, r.t1) - r.t0) * 365.25) + 1);
  const seek = (e: React.PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    seekReplay((e.clientX - box.left) / box.width);
  };
  return (
    <div className="replay-hud" data-testid="replay-hud" role="region" aria-label={`Replay: ${r.title}`}>
      <div className="rh-top">
        <span className="rh-label">Replay</span>
        <span className="rh-title">{r.title}</span>
        <span className="rh-clock" aria-label={`Day ${day} of the war`}>
          <span className="rh-day">Day {fmtInt(day)}</span>
          <span className="rh-now">{formatInstant(t, 1)}</span>
        </span>
        <SoundButton className="rh-sound" />
        <button className="rh-exit" onClick={endReplay} aria-label="Exit replay">×</button>
      </div>
      <div className={`rh-caption${live ? " live" : ""}`} aria-live="polite" data-testid="replay-caption">
        {r.done ? (
          <p className="rh-text">The end. <button className="link" onClick={restartReplay}>Play again</button> or <button className="link" onClick={endReplay}>explore the map</button>.</p>
        ) : ch ? (
          <div key={k} className="rh-chapter">
            <div className="rh-chno">Chapter {k + 1} of {r.chapters.length}</div>
            <div className="rh-date">{ch.d}</div>
            <Typewriter text={ch.x} live={live} />
          </div>
        ) : (
          <p className="rh-text rh-muted">{yearsLabel(r.t0, r.t1, false)} · battles appear on the map as they happen.</p>
        )}
      </div>
      <div className="rh-controls">
        <button onClick={() => stepChapter(-1)} aria-label="Previous chapter" disabled={!r.chapters.length}><Icon d={PREV} /></button>
        <button className="rh-play" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} data-testid="replay-play"><Icon d={playing ? PAUSE : PLAY} /></button>
        <button onClick={() => stepChapter(1)} aria-label="Next chapter" disabled={k + 1 >= r.chapters.length}><Icon d={NEXT} /></button>
        <div className="rh-progress" onPointerDown={seek} aria-hidden>
          <span className="rh-fill" style={{ width: `${u * 100}%` }} />
          {r.chapters.map((c, i) => (
            <span key={i} className={`rh-tick${i <= k ? " past" : ""}${i === k ? " here" : ""}`} style={{ left: `${progressAt(r.warp, c.t) * 100}%` }} title={`${c.d}: ${c.x}`} />
          ))}
        </div>
        <button className="rh-speed" onClick={() => setSpeed(speed >= 4 ? 0.5 : speed * 2)} aria-label="Playback speed" title="Playback speed">{speed}×</button>
      </div>
      {fxOn && (
        <div className="rh-stats" aria-live="off">
          <span title="Battles and events of this war on the map so far">{fmtInt(stats.battles)} battles</span>
          {stats.burning > 0 && <span className="rh-burning" title="Places burning on screen now">{stats.burning} burning</span>}
        </div>
      )}
    </div>
  );
}
